import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import GrButton from "./GrButton";
import { gr } from "../tokens";

// HTML written by the gym in the admin editor (a programme's or a video's description): body text 14 ink,
// calm headings, lists, images that fit the width and teal links. One look for every description, so
// pages pass `html` instead of styling the markup themselves.
// `collapsedLines` shows only that many lines of a long text with "View more" under it ("View less"
// folds it again); short texts show whole, with no link.
const richTextSx = {
  fontFamily: gr.font.family,
  fontSize: gr.font.size.md,
  color: gr.color.ink,
  lineHeight: gr.font.lineHeight.normal,
  overflowWrap: "anywhere",
  "& p": { m: 0, mb: gr.space[3] },
  "& p:last-child": { mb: 0 },
  "& h1, & h2, & h3, & h4": {
    m: 0,
    mb: gr.space[2],
    fontSize: gr.font.size.lg,
    fontWeight: gr.font.weight.semibold,
    color: gr.color.ink,
  },
  "& ul, & ol": { m: 0, mb: gr.space[3], pl: gr.space[6] },
  "& img": {
    display: "block",
    maxWidth: "100%",
    height: "auto",
    mx: "auto",
    borderRadius: gr.radius.tile,
  },
  "& a": { color: gr.color.brandDark },
};

export default function GrRichText({ html, collapsedLines, sx }) {
  const bodyRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Is the text longer than the folded height? (re-checked when the width changes)
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || !collapsedLines) return undefined;
    // Measured in the next frame: setting state inside the observer's own callback makes the browser
    // report a "ResizeObserver loop" when the text's height changes.
    let frame = 0;
    const check = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        setOverflows(el.scrollHeight - el.clientHeight > 1)
      );
    };
    check();
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null;
    if (observer) observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
    };
  }, [html, collapsedLines, expanded]);

  useEffect(() => setExpanded(false), [html]);

  const folded = Boolean(collapsedLines) && !expanded;
  return (
    <Box sx={sx}>
      <Box
        ref={bodyRef}
        sx={{
          ...richTextSx,
          ...(folded
            ? {
                maxHeight: `calc(${gr.font.size.md} * ${gr.font.lineHeight.normal} * ${collapsedLines})`,
                overflow: "hidden",
                // The cut line fades out instead of stopping halfway through the letters.
                ...(overflows
                  ? {
                      maskImage:
                        "linear-gradient(to bottom, black 60%, transparent)",
                      WebkitMaskImage:
                        "linear-gradient(to bottom, black 60%, transparent)",
                    }
                  : {}),
              }
            : {}),
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {collapsedLines && (overflows || expanded) ? (
        <GrButton
          variant="link"
          size="sm"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          sx={{ mt: gr.space[2] }}
        >
          {expanded ? "View less" : "View more"}
        </GrButton>
      ) : null}
    </Box>
  );
}

GrRichText.propTypes = {
  html: PropTypes.string.isRequired,
  collapsedLines: PropTypes.number,
  sx: PropTypes.object,
};
